import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CashDragCalculator from './components/CashDragCalculator';
import { trackPageview, trackOptIn } from '../../utils/analytics';

export default function HomePage() {
  const navigate = useNavigate();

  // Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://inflationdefense.com';
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: siteUrl,
          name: 'Inflation Defense Manual – Free PDF | Protect Savings from Inflation',
          description:
            'Download The Inflation Defense Manual free. Marine Corps Veteran Matt Theriault reveals hard asset strategies, inflation protection tactics, and financial self-defense secrets.',
          inLanguage: 'en-US',
          isPartOf: { '@id': `${siteUrl}/#website` },
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: 'https://public.readdy.ai/ai/img_res/09f4f033-88ad-4dc3-be42-30055db03ccd.png',
          },
          dateModified: '2025-01-15',
        },
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: 'Inflation Defense',
          publisher: { '@id': `${siteUrl}/#organization` },
        },
        {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'Epic Real Estate Investing',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: 'https://public.readdy.ai/ai/img_res/09f4f033-88ad-4dc3-be42-30055db03ccd.png',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'support@epicrealestateinvesting.com',
            contactType: 'customer support',
          },
        },
        {
          '@type': 'Book',
          name: 'The Inflation Defense Manual',
          author: {
            '@type': 'Person',
            name: 'Matt Theriault',
            jobTitle: 'Marine Corps Veteran & Real Estate Strategist',
          },
          description:
            'A tactical guide to protecting your savings from inflation theft using hard assets, real estate investing, and financial self-defense strategies.',
          inLanguage: 'en-US',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url: siteUrl,
          },
          image: 'https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/7e946c0821e3a512907908c461ea08e7.png',
        },
      ],
    };

    const existing = document.getElementById('schema-home');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-home';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-home');
      if (el) el.remove();
    };
  }, []);

  // Track pageview on mount
  useEffect(() => {
    trackPageview('/');
  }, []);

  // Hero form state (now for modal)
  const [heroEmail, setHeroEmail] = useState('');
  const [heroFirstName, setHeroFirstName] = useState('');
  const [heroPhone, setHeroPhone] = useState('');
  const [heroIsSubmitting, setHeroIsSubmitting] = useState(false);
  const [heroSubmitStatus, setHeroSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Final CTA form state
  const [ctaEmail, setCtaEmail] = useState('');
  const [ctaFirstName, setCtaFirstName] = useState('');
  const [ctaPhone, setCtaPhone] = useState('');
  const [ctaIsSubmitting, setCtaIsSubmitting] = useState(false);
  const [ctaSubmitStatus, setCtaSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Function to sanitize phone to E.164 format
  const sanitizePhoneToE164 = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    // If it starts with 1 and has 11 digits (US/Canada format), keep it
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    }
    
    // If it has 10 digits, assume US/Canada and add +1
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }
    
    // If it already has country code (more than 10 digits), add + if missing
    if (digitsOnly.length > 10) {
      return `+${digitsOnly}`;
    }
    
    // Default: add +1 for US/Canada
    return `+1${digitsOnly}`;
  };

  const handleHeroSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!heroEmail || !heroFirstName || !heroPhone) {
      return;
    }

    setHeroIsSubmitting(true);
    setHeroSubmitStatus('idle');

    try {
      // Sanitize phone to E.164 format
      const sanitizedPhone = sanitizePhoneToE164(heroPhone);

      // Track opt-in conversion (Step 1)
      await trackOptIn(heroEmail, heroFirstName, sanitizedPhone);

      // Prepare data for BookedIn.ai
      const bookedInData = {
        first_name: heroFirstName,
        email: heroEmail,
        phone: sanitizedPhone,
      };

      // Prepare data for Kit (form-urlencoded) - FIXED FIELD MAPPING
      const kitFormData = new URLSearchParams();
      kitFormData.append('email_address', heroEmail);
      kitFormData.append('fields[first_name]', heroFirstName);
      kitFormData.append('fields[phone]', sanitizedPhone);

      // Submit to BookedIn.ai (primary - must succeed)
      const bookedInResponse = await fetch('https://api.bookedin.ai/api/v1/strategies/str_XXbaDAyJWDw/webhook/whk_8YD4Hegifm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookedInData),
      });

      // Submit to Kit (secondary - don't block on failure) - UPDATED URL
      fetch('https://app.kit.com/forms/8839101/subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: kitFormData.toString(),
      }).catch(() => {
        // Silently fail Kit submission - don't block redirect
      });

      // Check if BookedIn submission was successful
      if (bookedInResponse.ok) {
        setHeroSubmitStatus('success');
        setHeroEmail('');
        setHeroFirstName('');
        setHeroPhone('');
        
        // Redirect to thank you page after short delay
        setTimeout(() => {
          navigate('/thank-you');
        }, 500);
      } else {
        setHeroSubmitStatus('error');
      }
    } catch (error) {
      setHeroSubmitStatus('error');
    } finally {
      setHeroIsSubmitting(false);
    }
  };

  const handleCtaSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!ctaEmail || !ctaFirstName || !ctaPhone) {
      return;
    }

    setCtaIsSubmitting(true);
    setCtaSubmitStatus('idle');

    try {
      // Sanitize phone to E.164 format
      const sanitizedPhone = sanitizePhoneToE164(ctaPhone);

      // Track opt-in conversion (Step 1)
      await trackOptIn(ctaEmail, ctaFirstName, sanitizedPhone);

      // Prepare data for BookedIn.ai
      const bookedInData = {
        first_name: ctaFirstName,
        email: ctaEmail,
        phone: sanitizedPhone,
      };

      // Prepare data for Kit (form-urlencoded) - FIXED FIELD MAPPING
      const kitFormData = new URLSearchParams();
      kitFormData.append('email_address', ctaEmail);
      kitFormData.append('fields[first_name]', ctaFirstName);
      kitFormData.append('fields[phone]', sanitizedPhone);

      // Submit to BookedIn.ai (primary - must succeed)
      const bookedInResponse = await fetch('https://api.bookedin.ai/api/v1/strategies/str_XXbaDAyJWDw/webhook/whk_8YD4Hegifm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookedInData),
      });

      // Submit to Kit (secondary - don't block on failure) - UPDATED URL
      fetch('https://app.kit.com/forms/8839101/subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: kitFormData.toString(),
      }).catch(() => {
        // Silently fail Kit submission - don't block redirect
      });

      // Check if BookedIn submission was successful
      if (bookedInResponse.ok) {
        setCtaSubmitStatus('success');
        setCtaEmail('');
        setCtaFirstName('');
        setCtaPhone('');
        
        // Redirect to thank you page after short delay
        setTimeout(() => {
          navigate('/thank-you');
        }, 500);
      } else {
        setCtaSubmitStatus('error');
      }
    } catch (error) {
      setCtaSubmitStatus('error');
    } finally {
      setCtaIsSubmitting(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setHeroSubmitStatus('idle');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e1a] via-[#111827] to-[#0a0e1a]">
      {/* Hero Section - Calculator First */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)`,
          }}></div>
        </div>

        {/* Tactical Grid Overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 197, 94, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-8">
          {/* Logo */}
          <div className="mb-6 sm:mb-8 flex justify-center lg:justify-start">
            <img 
              src="https://public.readdy.ai/ai/img_res/09f4f033-88ad-4dc3-be42-30055db03ccd.png" 
              alt="Inflation Defense" 
              className="h-10 sm:h-12 w-auto"
            />
          </div>

          {/* Calculator Component - Now in Hero */}
          <CashDragCalculator onStopLeakClick={handleOpenModal} />
        </div>
      </section>

      {/* Modal - Contains the exact same form */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-lg bg-[#1a1f2e] border border-gray-700 rounded-2xl shadow-2xl">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Modal Content */}
            <div className="p-6 sm:p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-black text-white mb-2">STOP THE BLEEDING.</h3>
                <p className="text-white font-semibold text-base mb-1">
                  Where should we send your Inflation Defense guide?
                </p>
                <p className="text-gray-400 text-sm">
                  Free Manual • Immediate Delivery
                </p>
              </div>

              {/* Exact same form from Hero */}
              <div data-readdy-form id="hero-form">
                <form onSubmit={handleHeroSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="first_name"
                      value={heroFirstName}
                      onChange={(e) => setHeroFirstName(e.target.value)}
                      placeholder="First Name"
                      required
                      className="w-full px-4 py-3 bg-[#0f1419] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-600 transition-colors text-sm"
                    />
                  </div>

                  <div>
                    <input
                      type="email"
                      name="email"
                      value={heroEmail}
                      onChange={(e) => setHeroEmail(e.target.value)}
                      placeholder="Email Address"
                      required
                      className="w-full px-4 py-3 bg-[#0f1419] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-600 transition-colors text-sm"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={heroPhone}
                      onChange={(e) => setHeroPhone(e.target.value)}
                      placeholder="Phone Number"
                      required
                      className="w-full px-4 py-3 bg-[#0f1419] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-600 transition-colors text-sm"
                    />
                    <p className="text-gray-400 text-xs mt-2 leading-relaxed">
                      By clicking submit, I consent to receive SMS updates and marketing messages from Epic Real Estate at the number provided. Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={heroIsSubmitting}
                    className="w-full px-6 py-3.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-green-600/50 disabled:opacity-50 disabled:cursor-not-allowed text-base whitespace-nowrap"
                  >
                    {heroIsSubmitting ? 'SENDING...' : 'SEND ME THE GUIDE →'}
                  </button>

                  {heroSubmitStatus === 'success' && (
                    <div className="text-green-500 text-sm text-center font-semibold">
                      ✓ Success! Check your email for the manual.
                    </div>
                  )}

                  {heroSubmitStatus === 'error' && (
                    <div className="text-red-500 text-sm text-center font-semibold">
                      Something went wrong. Please try again.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The Author Section - moved up, directly after calculator */}
      <section className="relative py-20 sm:py-28 bg-[#0a0e1a]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-[#1a1f2e]/60 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="grid md:grid-cols-5 gap-6 items-center">
              {/* Author Image */}
              <div className="md:col-span-2 pl-5 md:pl-7 py-6 pr-2">
                <div className="relative w-full overflow-hidden rounded-2xl">
                  <img 
                    src="https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/dadaf59f289a861c44051d9874a96e48.jpeg"
                    alt="Matt Theriault"
                    className="w-full h-full object-cover object-top rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1f2e] via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-[#1a1f2e] rounded-2xl"></div>
                </div>
              </div>

              {/* Author Bio */}
              <div className="md:col-span-3 p-6 md:pr-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-800/50 border border-gray-700 rounded-full mb-4">
                  <span className="text-gray-400 text-xs font-bold tracking-widest uppercase">Your Guide</span>
                </div>
                
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white mb-5 tracking-tight">
                  <span className="text-green-500">MATT THERIAULT</span>
                </h2>
                
                <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-5 sm:mb-6">
                  Matt Theriault is not a 'Guru.' He's a <span className="text-white font-semibold">Marine Corps Veteran</span> and <span className="text-white font-semibold">Real Estate Strategist</span> who helps everyday homeowners build a fortress around their wealth.
                </p>
                
                <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                  He exposes the systems rigged against you and hands you the playbook to fight back.
                </p>

                <div className="mt-6 flex items-center gap-3">
                  <div className="w-1 h-12 bg-green-600"></div>
                  <div>
                    <div className="text-green-500 text-sm font-bold uppercase tracking-wider">Mission</div>
                    <div className="text-white font-semibold">Financial Self-Defense</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-16 sm:py-24 bg-gradient-to-b from-[#0a0e1a] to-[#0f1419]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          {/* Book Mockup */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="relative w-72 h-96 sm:w-96 sm:h-[32rem]">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/40 via-blue-500/30 to-transparent blur-3xl scale-125"></div>
              <div className="relative z-10">
                <img 
                  src="https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/7e946c0821e3a512907908c461ea08e7.png"
                  alt="Defense Manual"
                  className="w-full h-auto drop-shadow-2xl"
                  style={{
                    filter: 'drop-shadow(0 25px 50px rgba(6, 182, 212, 0.5))'
                  }}
                />
              </div>
              <div className="absolute inset-0 animate-pulse pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-cyan-500/10 blur-3xl"></div>
              </div>
            </div>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 sm:mb-8 tracking-tight">
            STOP THE BLEEDING.
          </h2>

          {/* Final CTA Form */}
          <div className="max-w-2xl mx-auto">
            <div data-readdy-form id="final-cta-form" className="bg-[#1a1f2e]/80 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-4 sm:p-6 lg:p-7 shadow-2xl">
              <div className="text-center mb-4 sm:mb-5">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5">Where should we send your Inflation Defense guide?</h3>
                <p className="text-gray-400 text-xs sm:text-sm">Free Manual • Immediate Delivery</p>
              </div>

              <form onSubmit={handleCtaSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <input
                    type="text"
                    name="first_name"
                    value={ctaFirstName}
                    onChange={(e) => setCtaFirstName(e.target.value)}
                    placeholder="First Name"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#0f1419] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-600 transition-colors text-sm"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={ctaEmail}
                    onChange={(e) => setCtaEmail(e.target.value)}
                    placeholder="Email Address"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#0f1419] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-600 transition-colors text-sm"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={ctaPhone}
                    onChange={(e) => setCtaPhone(e.target.value)}
                    placeholder="Phone Number"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#0f1419] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-600 transition-colors text-sm"
                  />
                  <p className="text-gray-400 text-xs mt-2 leading-relaxed">
                    By clicking submit, I consent to receive SMS updates and marketing messages from Epic Real Estate at the number provided. Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={ctaIsSubmitting}
                  className="w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-green-600/50 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base whitespace-nowrap"
                >
                  {ctaIsSubmitting ? 'SENDING...' : 'GET MY FREE GUIDE'}
                </button>

                {ctaSubmitStatus === 'success' && (
                  <div className="text-green-500 text-sm text-center font-semibold">
                    ✓ Success! Check your email for the manual.
                  </div>
                )}

                {ctaSubmitStatus === 'error' && (
                  <div className="text-red-500 text-sm text-center font-semibold">
                    Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-10 bg-[#0a0e1a] border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-gray-500 text-sm">
              © 2026 Epic Real Estate Investing. All Rights Reserved.
            </div>
            
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer">
                Privacy Policy
              </a>
              <span className="text-gray-700">|</span>
              <a href="/terms" className="text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer">
                Terms of Service
              </a>
              <span className="text-gray-700">|</span>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer">
                Powered by Readdy
              </a>
              <span className="text-gray-700">|</span>
              <a href="/admin/login" className="text-gray-700 hover:text-gray-500 text-xs transition-colors cursor-pointer">
                Admin
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
